/**
 * Local OCR Service using Tesseract.js
 * 
 * Provides browser-based OCR functionality without requiring external APIs.
 * Uses Tesseract.js which runs entirely in the browser via WebAssembly.
 */

import Tesseract from 'tesseract.js';

// Cache the worker for reuse
let workerInstance = null;
let workerReady = false;

/**
 * Initialize the Tesseract worker (call once at startup for faster subsequent OCR)
 * @returns {Promise<void>}
 */
export async function initializeOcr() {
  if (workerReady) return;
  
  try {
    workerInstance = await Tesseract.createWorker('eng', 1, {
      logger: (m) => {
        if (m.status === 'recognizing text') {
          // Progress callback if needed
          console.log(`OCR Progress: ${Math.round(m.progress * 100)}%`);
        }
      }
    });
    workerReady = true;
    console.log('Tesseract OCR initialized');
  } catch (error) {
    console.error('Failed to initialize Tesseract:', error);
    throw error;
  }
}

/**
 * Perform OCR on an image
 * @param {string|HTMLCanvasElement|HTMLImageElement|Blob} image - Image to process
 * @param {Object} options - OCR options
 * @param {Function} options.onProgress - Progress callback (0-100)
 * @returns {Promise<Object>} - OCR result with text and confidence
 */
export async function performOcr(image, options = {}) {
  const { onProgress } = options;
  
  try {
    // Use cached worker or create new one
    let worker = workerInstance;
    
    if (!worker || !workerReady) {
      worker = await Tesseract.createWorker('eng', 1, {
        logger: (m) => {
          if (m.status === 'recognizing text' && onProgress) {
            onProgress(Math.round(m.progress * 100));
          }
        }
      });
    }
    
    const result = await worker.recognize(image);
    
    // Debug: log result structure
    console.log('Tesseract result:', result);
    
    // Handle different Tesseract.js result structures
    const data = result.data || result;
    const text = data.text || '';
    const confidence = data.confidence || 0;
    
    // Safely map lines if they exist
    const lines = (data.lines || []).map(line => ({
      text: line?.text || '',
      confidence: line?.confidence || 0,
      bbox: line?.bbox || {},
      words: (line?.words || []).map(w => ({
        text: w?.text || '',
        confidence: w?.confidence || 0,
        bbox: w?.bbox || {}
      }))
    }));
    
    return {
      success: true,
      text,
      confidence,
      lines,
      blocks: data.blocks || [],
      paragraphs: data.paragraphs || []
    };
  } catch (error) {
    console.error('OCR failed:', error);
    return {
      success: false,
      error: error.message,
      text: '',
      lines: []
    };
  }
}

/**
 * Extract a region from a canvas and perform OCR
 * @param {HTMLCanvasElement} canvas - Source canvas
 * @param {Object} region - Region coordinates {x, y, width, height} as percentages (0-100)
 * @param {Object} options - OCR options
 * @returns {Promise<Object>} - OCR result
 */
export async function performOcrOnRegion(canvas, region, options = {}) {
  // Create a new canvas for the cropped region
  const cropCanvas = document.createElement('canvas');
  const ctx = cropCanvas.getContext('2d');
  
  // Calculate actual pixel coordinates from percentages
  const x = Math.round((region.x / 100) * canvas.width);
  const y = Math.round((region.y / 100) * canvas.height);
  const width = Math.round((region.width / 100) * canvas.width);
  const height = Math.round((region.height / 100) * canvas.height);
  
  // Set crop canvas size
  cropCanvas.width = width;
  cropCanvas.height = height;
  
  // Draw the cropped region
  ctx.drawImage(canvas, x, y, width, height, 0, 0, width, height);
  
  // Enhance image for better OCR (increase contrast, convert to grayscale)
  enhanceForOcr(ctx, width, height);
  
  // Perform OCR on the cropped region
  return performOcr(cropCanvas, options);
}

/**
 * Enhance image for better OCR results
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {number} width - Width
 * @param {number} height - Height
 */
function enhanceForOcr(ctx, width, height) {
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  // Convert to grayscale and increase contrast
  for (let i = 0; i < data.length; i += 4) {
    // Grayscale using luminosity method
    const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
    
    // Increase contrast
    const contrast = 1.5;
    const factor = (259 * (contrast * 255 + 255)) / (255 * (259 - contrast * 255));
    const enhanced = Math.min(255, Math.max(0, factor * (gray - 128) + 128));
    
    // Apply threshold for cleaner text
    const threshold = enhanced > 180 ? 255 : enhanced < 80 ? 0 : enhanced;
    
    data[i] = threshold;     // R
    data[i + 1] = threshold; // G
    data[i + 2] = threshold; // B
    // Alpha stays the same
  }
  
  ctx.putImageData(imageData, 0, 0);
}

/**
 * Terminate the OCR worker to free resources
 */
export async function terminateOcr() {
  if (workerInstance) {
    await workerInstance.terminate();
    workerInstance = null;
    workerReady = false;
  }
}

const localOcr = {
  initializeOcr,
  performOcr,
  performOcrOnRegion,
  terminateOcr
};

export default localOcr;
